import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import IconMC from './IconMC';
import useClickOutside from '../../hooks/useClickOutside';

/** @constant {Array<{id: string, labelKey: string}>} Comparison options for the dropdown (greater than / less than). */
const COMPARISON_OPTIONS = [
  { id: 'greater_than', labelKey: 'comparison.greater_than' },
  { id: 'less_than', labelKey: 'comparison.less_than' }
];

/**
 * Clamps a numeric value to a range. Returns empty string for NaN.
 * @param {number|string} value - Value to clamp.
 * @param {number} min - Minimum allowed value.
 * @param {number} max - Maximum allowed value.
 * @returns {number|''} Clamped number or empty string.
 */
const clampNumber = (value, min, max) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  if (num < min) return min;
  if (num > max) return max;
  return num;
};

const inputBaseClassName = 'tw-w-full tw-px-2 tw-py-1.5 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500';
const inputWithSuffixClassName = `${inputBaseClassName} tw-pr-8`;
const suffixClassName = 'tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-xs tw-pointer-events-none';

/**
 * Duration comparison input: three numeric fields (hours, minutes, seconds) plus a comparison operator select (greater than / less than).
 * Each time input shows a unit suffix on the right (h, min, s). Values are constrained (hours 0–23, minutes/seconds 0–59).
 *
 * @component
 * @param {Object} props - Component props.
 * @param {DurationComparisonOnChange} [props.onChange] - Called when any value changes. Receives the current duration and comparison.
 * @param {number|string} [props.defaultHours=''] - Initial hours (0–23).
 * @param {number|string} [props.defaultMinutes=''] - Initial minutes (0–59).
 * @param {number|string} [props.defaultSeconds=''] - Initial seconds (0–59).
 * @param {'greater_than'|'less_than'} [props.defaultComparison='greater_than'] - Initial comparison operator.
 * @param {string} [props.className=''] - Extra CSS class for the root container.
 * @param {boolean} [props.disabled=false] - When true, all inputs and the select are disabled.
 * @returns {React.ReactElement}
 *
 * @typedef {Object} DurationComparisonValue
 * @property {number|string} hours - Hours (0–23).
 * @property {number|string} minutes - Minutes (0–59).
 * @property {number|string} seconds - Seconds (0–59).
 * @property {'greater_than'|'less_than'} comparison - Selected comparison operator.
 *
 * @typedef { (value: DurationComparisonValue) => void } DurationComparisonOnChange
 *
 * @example
 * <DurationComparisonInputMC
 *   onChange={({ hours, minutes, seconds, comparison }) => setFilter({ hours, minutes, seconds, comparison })}
 *   defaultHours={0}
 *   defaultMinutes={5}
 *   defaultSeconds={30}
 *   defaultComparison="greater_than"
 * />
 */
const DurationComparisonInputMC = ({
  onChange,
  defaultHours = '',
  defaultMinutes = '',
  defaultSeconds = '',
  defaultComparison = 'greater_than',
  className = '',
  disabled = false
}) => {
  const { t } = useTranslation();
  const [hours, setHours] = useState(defaultHours);
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [seconds, setSeconds] = useState(defaultSeconds);
  const [comparison, setComparison] = useState(defaultComparison);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const domNode = useClickOutside(() => setIsDropdownOpen(false));

  const prevDefaultsRef = useRef({
    defaultHours,
    defaultMinutes,
    defaultSeconds,
    defaultComparison
  });

  useEffect(() => {
    const prev = prevDefaultsRef.current;
    if (
      prev.defaultHours !== defaultHours
      || prev.defaultMinutes !== defaultMinutes
      || prev.defaultSeconds !== defaultSeconds
      || prev.defaultComparison !== defaultComparison
    ) {
      prevDefaultsRef.current = {
        defaultHours,
        defaultMinutes,
        defaultSeconds,
        defaultComparison
      };
      setHours(defaultHours);
      setMinutes(defaultMinutes);
      setSeconds(defaultSeconds);
      setComparison(defaultComparison);
    }
  }, [defaultHours, defaultMinutes, defaultSeconds, defaultComparison]);

  const notifyChange = (nextHours, nextMinutes, nextSeconds, nextComparison) => {
    if (onChange) {
      onChange({
        hours: nextHours,
        minutes: nextMinutes,
        seconds: nextSeconds,
        comparison: nextComparison
      });
    }
  };

  const handleHoursChange = (e) => {
    const raw = e.target.value;
    const value = raw === '' ? '' : clampNumber(raw, 0, 23);
    setHours(value);
    notifyChange(value, minutes, seconds, comparison);
  };

  const handleMinutesChange = (e) => {
    const raw = e.target.value;
    const value = raw === '' ? '' : clampNumber(raw, 0, 59);
    setMinutes(value);
    notifyChange(hours, value, seconds, comparison);
  };

  const handleSecondsChange = (e) => {
    const raw = e.target.value;
    const value = raw === '' ? '' : clampNumber(raw, 0, 59);
    setSeconds(value);
    notifyChange(hours, minutes, value, comparison);
  };

  const handleComparisonSelect = (comparisonId) => {
    setComparison(comparisonId);
    setIsDropdownOpen(false);
    notifyChange(hours, minutes, seconds, comparisonId);
  };

  const selectedOption = COMPARISON_OPTIONS.find((opt) => opt.id === comparison);
  const selectedComparisonLabel = selectedOption
    ? t(selectedOption.labelKey)
    : t('comparison.greater_than');

  return (
    <div className={`tw-flex tw-flex-wrap tw-gap-1 tw-items-end ${className}`}>
      <div className="tw-relative tw-min-w-0" style={{ flex: '1 1 0' }}>
        <input
          id="duration-hours"
          type="number"
          min={0}
          max={23}
          value={hours}
          onChange={handleHoursChange}
          placeholder="0"
          disabled={disabled}
          className={inputWithSuffixClassName}
        />
        <span className={suffixClassName}>h</span>
      </div>

      <div className="tw-relative tw-min-w-0" style={{ flex: '1 1 0' }}>
        <input
          id="duration-minutes"
          type="number"
          min={0}
          max={59}
          value={minutes}
          onChange={handleMinutesChange}
          placeholder="0"
          disabled={disabled}
          className={inputWithSuffixClassName}
        />
        <span className={suffixClassName}>min</span>
      </div>

      <div className="tw-relative tw-min-w-0" style={{ flex: '1 1 0' }}>
        <input
          id="duration-seconds"
          type="number"
          min={0}
          max={59}
          value={seconds}
          onChange={handleSecondsChange}
          placeholder="0"
          disabled={disabled}
          className={inputWithSuffixClassName}
        />
        <span className={suffixClassName}>s</span>
      </div>

      <div className="tw-relative tw-min-w-[140px]" ref={domNode} style={{ flex: '1 1 140px' }}>
        <button
          id="duration-comparison"
          type="button"
          onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
          disabled={disabled}
          className="tw-bg-gray-50 tw-rounded-lg tw-w-full tw-px-2 tw-py-1.5 tw-flex tw-items-center tw-justify-between tw-border tw-border-gray-200 tw-text-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          <span className="tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
            {selectedComparisonLabel}
          </span>
          <IconMC
            name={`arrow-ios-${isDropdownOpen ? 'up' : 'down'}ward-outline`}
            size="base"
            className="tw-text-gray-600 tw-shrink-0"
          />
        </button>
        {isDropdownOpen && (
          <div className="tw-absolute tw-bg-white tw-border tw-rounded-md tw-shadow-lg tw-z-50 tw-w-full tw-mt-0.5">
            <ul className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-pl-0 tw-m-0 tw-list-none">
              {COMPARISON_OPTIONS.map((option) => (
                <li
                  key={option.id}
                  role="option"
                  aria-selected={comparison === option.id}
                  onClick={() => handleComparisonSelect(option.id)}
                  className={`tw-px-4 tw-py-2 tw-cursor-pointer tw-text-sm hover:tw-bg-blue-100 ${
                    comparison === option.id ? 'tw-bg-blue-500 tw-text-white' : ''
                  }`}
                >
                  {t(option.labelKey)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default DurationComparisonInputMC;
